import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Stack,
  Box,
  Paper,
  Chip,
  Card,
  Drawer,
} from '@mui/material';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateFilterDataById } from '../../services/playlists.service';
import ListData from '../table-elements/list-data.component';
import FilterPlaylistVideos from '../filter-videos/filter-playlist-video.component';
import { getListData } from '../../services/videos.service';
import { FiltersContext } from '../../context/filter.context';
import { LISTING_COLUMNS } from '../../config/module-configs/manage-videos.config';
import Iconify from '../common/iconify.component';
import { formatTimestamp } from '../../utils/datetime.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';
import { VIDEO_SOURCES } from '../../config/const.config';

const PlaylistVideos = ({ playlistDetail, handleRefreshData }) => {
  const columns = LISTING_COLUMNS;
  const { getSignal, isAbortError } = useAbortableRequest();

  const defaultValueRef = useRef([]);
  const { handleToggleFilters } = useContext(FiltersContext);

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      ...playlistDetail.filters,
      licenese_expired_videos: {
        apply: true,
        condition: 'EQUALS_TO',
        title: 'Show License Expired Videos',
        type: 'checkbox',
        value: false,
      },
      video_source: {
        apply: true,
        title: 'Video Source',
        type: 'selectType',
        value: playlistDetail.checkYouTubePlaylist
          ? VIDEO_SOURCES.YOUTUBE.toUpperCase()
          : VIDEO_SOURCES.FILE.toUpperCase(),
      },
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleVideosListRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    const counter =
      Object.values(selectedFilters).filter((filter) => filter.apply).length ||
      0;

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
        video_source: {
          apply: true,
          title: 'Video Source',
          type: 'selectType',
          value: playlistDetail.checkYouTubePlaylist
            ? VIDEO_SOURCES.YOUTUBE.toUpperCase()
            : VIDEO_SOURCES.FILE.toUpperCase(),
        },
      },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });

    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    updateFilterDataById(playlistDetail.id, {
      filters: { ...selectedFilters },
    })
      .then((res) => {
        if (res.data.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Playlist filters saved successfully',
          });
          handleRefreshData();
          setFormSubmitted(false);
        }
      })
      .catch((err) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            err.response?.data?.message ||
            'Something went wrong, please try again.',
        });
        setFormSubmitted(false);
      });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleClearFilters = () => {
    setOptions({
      ...options,
      appliedFilters: {
        licenese_expired_videos: {
          apply: true,
          condition: 'EQUALS_TO',
          title: 'Show License Expired Videos',
          type: 'checkbox',
          value: false,
        },
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }

    const updatedFilters = { ...options.appliedFilters };
    if (field === 'licenese_expired_videos') {
      updatedFilters[field] = { ...updatedFilters[field], value: false };
    } else {
      delete updatedFilters[field];

      updatedFilters[field] = {
        ...updatedFilters[field],
        value: Array.isArray(updatedFilters[field]?.value) ? [] : null || '',
      };
    }

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const signal = getSignal();
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const appliedFilters = Object.entries(options.appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));

    if (appliedFilters.search) {
      params.push(`q=${encodeURIComponent(appliedFilters.search)}`);
    }

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(
      uri + paramsQuery,
      {
        videoIds: defaultValueRef.current,
        filter: appliedFilters,
      },
      signal
    )
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Box
        display="flex"
        width="calc(100% - 290px)"
        component={Paper}
        padding={2}
      >
        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ px: 0, py: 2 }}
          >
            <Box display="flex" gap={2} alignItems="center">
              {!!playlistDetail.lastRefreshedAt && (
                <Typography variant="body2" color="secondary">
                  Last Synced At:{' '}
                  {formatTimestamp(
                    playlistDetail.lastRefreshedAt || null,
                    'lll'
                  )}
                </Typography>
              )}
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleVideosListRefreshData}
                disabled={options.loading}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          {options.appliedFilters &&
            Object.values(options.appliedFilters).some(
              (filter) =>
                filter.apply &&
                ((filter.type === 'checkbox' && filter.value) ||
                  filter.type !== 'checkbox')
            ) && (
              <Box mb={2}>
                <Typography variant="body1" fontWeight="bold" display="inline">
                  Filters Applied:{' '}
                  <Chip
                    key="all"
                    label="Clear all"
                    onDelete={() => handleDeleteFilter('clear-all')}
                    sx={{ ml: 1, color: 'red' }}
                  />
                </Typography>
                {Object.entries(options.appliedFilters)
                  .filter(
                    ([, filter]) =>
                      filter.apply &&
                      ((filter.type === 'checkbox' && filter.value) ||
                        filter.type !== 'checkbox')
                  )
                  .map(([field, filter]) => (
                    <Chip
                      key={field}
                      label={filter.title || 'Active'}
                      onDelete={() => handleDeleteFilter(field)}
                      sx={{ ml: 1 }}
                    />
                  ))}
              </Box>
            )}

          <Card>
            <ListData
              columns={columns}
              rows={options.rows}
              page={options.page}
              rowsPerPage={options.rowsPerPage}
              totalRows={options.totalRows}
              loading={options.loading}
              actions={[]}
              error={options.error}
              sortBy={options.sortBy}
              sortOrder={options.sortOrder}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSortChange={handleSortingChange}
              onAction={handleActionSelect}
            />
          </Card>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: 290,
            overflow: 'auto',
            height: '89.5%',
            top: '10.8%',
            right: 0,
          },
        }}
      >
        <FilterPlaylistVideos
          values={options.appliedFilters}
          onCancel={handleToggleFilters}
          onSuccess={handleApplyFilters}
          isFormSubmitted={formSubmitted}
          from="video-assignment"
        />
      </Drawer>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

PlaylistVideos.propTypes = {
  playlistDetail: PropTypes.object.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
};

export default PlaylistVideos;
